import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import { ChatService } from './chat.service';
import type { ConversationSummary } from './core/hub-types';
import {
  NewConversationData,
  NewConversationDialogComponent,
  NewConversationResult,
} from './components/new-conversation-dialog/new-conversation-dialog.component';

/** The patient a money card is about: the clinical patient, their practice and their name. */
export interface CardPatient {
  id: string;
  groupId: string;
  title: string;
}

/**
 * A money card's Chat button on the Connect page: the latest billing chat about the patient, or, the first time, a
 * picker of the billing team. Either way the chat opens with the patient tagged in its box.
 */
@Injectable({ providedIn: 'root' })
export class PatientCardChatService {
  constructor(
    private readonly chat: ChatService,
    private readonly dialog: MatDialog,
  ) {}

  async open(patient: CardPatient): Promise<void> {
    const [latest] = await this.chat.patientBillingChats(patient);
    if (latest) await this.chat.openWithPatient(latest.id, patient);
    else await this.startNew(patient);
  }

  /** The billing chats about the patient, latest first, for the card's menu. */
  chats(patient: CardPatient): Promise<ConversationSummary[]> {
    return this.chat.patientBillingChats(patient);
  }

  /** One of the chats about the patient, from the card's menu. */
  openChat(conversationId: string, patient: CardPatient): Promise<void> {
    return this.chat.openWithPatient(conversationId, patient);
  }

  async startNew(patient: CardPatient): Promise<void> {
    const [team] = await this.chat.billingTeams(patient.groupId);
    if (!team) return;
    const data: NewConversationData = { mode: 'billing', orgId: team.orgId, patient: { title: patient.title, treatingClinicalUserId: null } };
    const ref = this.dialog.open<NewConversationDialogComponent, NewConversationData, NewConversationResult>(NewConversationDialogComponent, {
      data,
      width: '480px',
      autoFocus: false,
    });
    const result = await firstValueFrom(ref.afterClosed());
    if (result) await this.chat.openWithPatient(result.conversationId, patient);
  }

  /** "Ben Biller · ClaimCare", as the chat list names it. */
  titleOf(c: ConversationSummary): string {
    return this.chat.store.titleOf(c);
  }
}
